import { useEffect, useRef, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDb } from '../lib/firebase';
import type { JobDoc } from '../lib/types';

export interface JobListItem {
  id: string;
  job: JobDoc;
}

const LIB_LOG = '[moments:library]';

/** Live list of the signed-in user’s jobs (newest first). */
export function useMyJobs(): { jobs: JobListItem[]; loading: boolean } {
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [loading, setLoading] = useState(true);
  /** After first non-null user, null means sign-out (not the transient pre-auth null). */
  const hadUserRef = useRef(false);

  useEffect(() => {
    const auth = getAuth();
    let unsubJobs: (() => void) | undefined;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      unsubJobs?.();
      if (!user) {
        setJobs([]);
        if (hadUserRef.current) {
          console.log(`${LIB_LOG} useMyJobs: user signed out — cleared project list`);
          setLoading(false);
        } else {
          // Wait for first sign-in (phone); do not treat as “loaded, 0 jobs”.
          console.log(`${LIB_LOG} useMyJobs: no auth user yet — keeping loading until sign-in`);
          setLoading(true);
        }
        return;
      }
      hadUserRef.current = true;
      setLoading(true);
      console.log(`${LIB_LOG} useMyJobs: loading projects for uid …${user.uid.slice(-6)}`);
      const db = getDb();
      const q = query(
        collection(db, 'users', user.uid, 'projects'),
        orderBy('createdAt', 'desc'),
        limit(40),
      );
      unsubJobs = onSnapshot(
        q,
        (snap) => {
          const list: JobListItem[] = [];
          snap.forEach((d) => {
            list.push({ id: d.id, job: d.data() as JobDoc });
          });
          setJobs(list);
          setLoading(false);
          console.log(
            `${LIB_LOG} useMyJobs: Firestore snapshot — ${list.length} project(s) (metadata under users/…/projects)`,
            { ids: list.map((x) => x.id) },
          );
        },
        (err) => {
          console.error(`${LIB_LOG} useMyJobs: snapshot error`, err);
          setLoading(false);
        },
      );
    });

    return () => {
      unsubJobs?.();
      unsubAuth();
    };
  }, []);

  return { jobs, loading };
}
